import { useEffect, useState } from "react";
import { Link2, Linkedin, Mail, Share2 } from "lucide-react";

interface Props {
  title: string;
  url: string;
  orientation?: "horizontal" | "vertical";
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M18.244 2H21l-6.52 7.45L22 22h-6.78l-4.7-6.59L4.8 22H2l7.04-8.04L2 2h6.86l4.26 5.98L18.24 2Zm-1.19 18h1.65L7.04 4H5.3l11.76 16Z" />
    </svg>
  );
}

export function ShareBar({ title, url, orientation = "horizontal" }: Props) {
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && "share" in navigator);
  }, []);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* no-op */
    }
  }

  async function nativeShare() {
    try {
      await navigator.share({ title, url });
    } catch {
      /* user cancelled */
    }
  }

  const buttonBase =
    "inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  const wrapperClass =
    orientation === "vertical"
      ? "flex flex-col items-start gap-2"
      : "flex flex-wrap items-center gap-2";

  return (
    <div className={wrapperClass} aria-label="Beitrag teilen">
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Auf LinkedIn teilen"
        className={buttonBase}
      >
        <Linkedin className="h-4 w-4" />
      </a>
      <a
        href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Auf X teilen"
        className={buttonBase}
      >
        <XIcon className="h-4 w-4" />
      </a>
      <a
        href={`mailto:?subject=${encodedTitle}&body=${encodedUrl}`}
        aria-label="Per E-Mail teilen"
        className={buttonBase}
      >
        <Mail className="h-4 w-4" />
      </a>
      <button
        type="button"
        onClick={copyLink}
        aria-label={copied ? "Link kopiert" : "Link kopieren"}
        className={buttonBase}
      >
        <Link2 className="h-4 w-4" />
      </button>
      {canNativeShare && (
        <button
          type="button"
          onClick={nativeShare}
          aria-label="Teilen"
          className={buttonBase}
        >
          <Share2 className="h-4 w-4" />
        </button>
      )}
      {copied && (
        <span
          role="status"
          className="font-body text-xs text-primary"
        >
          Link kopiert
        </span>
      )}
    </div>
  );
}
